<template>
  <div class="fdf">
    <!-- Conditions list -->
    <div
      v-if="conditions.length > 0"
      class="fdf-list"
    >
      <div
        v-for="(cond, ci) in conditions"
        :key="ci"
        class="fdf-cond"
        :class="{ 'fdf-cond--off': cond.isactive === false }"
      >
        <input
          type="checkbox"
          :checked="cond.isactive !== false"
          class="fdf-cond-check"
          @change="toggleCond(ci, $event.target.checked)"
        >
        <div class="fdf-cond-body">
          <div class="fdf-cond-sentence">
            {{ condSentence(cond) }}
          </div>
          <div class="fdf-cond-detail">
            {{ condDetail(cond) }}
          </div>
        </div>
        <span
          v-if="condTag(cond)"
          class="fdf-cond-tag"
        >{{ condTag(cond) }}</span>
        <span
          class="fdf-cond-del"
          title="Удалить"
          @click="removeCond(ci)"
        >✕</span>
      </div>
    </div>
    <div
      v-else
      class="fdf-empty"
    >
      <feather-icon
        icon="FilterIcon"
        size="14"
        class="mr-50"
      />
      Нет условий. Добавьте фильтры ниже.
    </div>

    <!-- Suggestions -->
    <div
      v-if="suggestions.length > 0"
      class="fdf-suggest"
    >
      <div class="fdf-suggest-title">
        Добавить:
      </div>
      <div
        v-for="(s, si) in suggestions"
        :key="si"
        class="fdf-suggest-item"
        @click="applySuggestion(s)"
      >
        <feather-icon
          icon="PlusCircleIcon"
          size="13"
          class="fdf-suggest-icon"
        />
        <span>{{ s.label }}</span>
      </div>
    </div>

    <!-- Custom condition form -->
    <div class="fdf-custom">
      <button
        v-if="!showAddForm"
        class="fdf-add-btn"
        @click="showAddForm = true"
      >
        <feather-icon
          icon="SlidersIcon"
          size="12"
        />
        Своё условие
      </button>

      <div
        v-if="showAddForm"
        class="fdf-add-form"
      >
        <div class="fdf-add-step">
          <span class="fdf-add-num">1</span>
          <span class="fdf-add-q">Какое поле фильтровать?</span>
          <select
            v-model="newCond.field"
            class="fdf-add-select"
          >
            <option :value="null">
              — выберите поле —
            </option>
            <option
              v-for="f in availableFields"
              :key="f.entityId"
              :value="f.entityId"
            >
              {{ f.name }}
            </option>
          </select>
        </div>
        <div
          v-if="newCond.field"
          class="fdf-add-step"
        >
          <span class="fdf-add-num">2</span>
          <span class="fdf-add-q">Какое условие?</span>
          <select
            v-model="newCond.oper"
            class="fdf-add-select"
          >
            <option value="equal">
              Равно
            </option>
            <option value="notequal">
              Не равно
            </option>
            <option value="greater">
              Больше
            </option>
            <option value="less">
              Меньше
            </option>
            <option value="like">
              Содержит текст
            </option>
            <option value="isnull">
              Пустое
            </option>
            <option value="startwith">
              Начинается с
            </option>
            <option value="inbranch">
              В ветке (иерархия)
            </option>
          </select>
        </div>
        <div
          v-if="newCond.field && newCond.oper !== 'isnull'"
          class="fdf-add-step"
        >
          <span class="fdf-add-num">3</span>
          <span class="fdf-add-q">Чему должно быть равно?</span>
          <select
            v-model="newCond.sourceType"
            class="fdf-add-select fdf-add-select--source"
          >
            <option value="paramentity">
              Параметру формы (автоматически)
            </option>
            <option value="value">
              Конкретному значению
            </option>
            <option value="scalar">
              Скаляру (вычисляемое)
            </option>
          </select>
          <div
            v-if="newCond.sourceType === 'value'"
            class="fdf-add-value"
          >
            <select
              v-if="selectedFieldDatatype === 'bit'"
              v-model="newCond.boolValue"
              class="fdf-add-select"
            >
              <option :value="false">
                false (нет)
              </option>
              <option :value="true">
                true (да)
              </option>
            </select>
            <input
              v-else
              v-model="newCond.value"
              class="fdf-add-input"
              :placeholder="'Введите ' + selectedFieldName"
            >
          </div>
          <div
            v-if="newCond.sourceType === 'scalar'"
            class="fdf-add-value"
          >
            <input
              v-model.number="newCond.scalarId"
              type="number"
              class="fdf-add-input"
              placeholder="ID скаляра"
            >
          </div>
        </div>
        <div
          v-if="newCond.field"
          class="fdf-add-preview"
        >
          Результат: <strong>{{ addPreview }}</strong>
        </div>
        <div class="fdf-add-actions">
          <button
            class="fdf-btn fdf-btn--primary"
            :disabled="!canAdd"
            @click="addCondition"
          >
            Добавить
          </button>
          <button
            class="fdf-btn"
            @click="showAddForm = false"
          >
            Отмена
          </button>
        </div>
      </div>
    </div>

    <!-- Footer links -->
    <div class="fdf-footer">
      <span
        class="fdf-footer-link"
        @click="$emit('toggle-advanced')"
      >Расширенный редактор (для разработчиков)</span>
      <span
        class="fdf-footer-link"
        @click="$emit('toggle-json')"
      >JSON</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CbsFormDesignerFilter',
  props: {
    filter: { type: Object, default: null },
    entityMap: { type: Object, default: () => ({}) },
    paletteFields: { type: Array, default: () => [] },
    params: { type: Array, default: () => [] },
    parentResults: { type: Array, default: () => [] },
  },
  data() {
    return {
      showAddForm: false,
      newCond: {
        field: null, oper: 'equal', sourceType: 'paramentity', value: '', boolValue: false, scalarId: null,
      },
    }
  },
  computed: {
    conditions() {
      if (!this.filter || !this.filter.node) return []
      return this.filter.node.conds || []
    },
    availableFields() {
      return this.paletteFields.length > 0
        ? this.paletteFields
        : Object.entries(this.entityMap).map(([id, info]) => ({ entityId: Number(id), name: info.name, datatype: info.datatype }))
    },
    selectedFieldName() {
      if (!this.newCond.field) return ''
      return this.entName(this.newCond.field)
    },
    selectedFieldDatatype() {
      if (!this.newCond.field) return ''
      const info = this.entityMap[this.newCond.field]
      if (info) return info.datatype
      const pf = this.paletteFields.find(f => f.entityId === this.newCond.field)
      return pf ? pf.datatype : ''
    },
    canAdd() {
      if (!this.newCond.field) return false
      if (this.newCond.oper === 'isnull') return true
      if (this.newCond.sourceType === 'scalar') return !!this.newCond.scalarId
      return true
    },
    addPreview() {
      const name = this.selectedFieldName
      const op = {
        equal: '=', notequal: '≠', greater: '>', less: '<', like: 'содержит', isnull: 'пусто', startwith: 'начинается с', inbranch: 'в ветке',
      }[this.newCond.oper] || '?'
      if (this.newCond.oper === 'isnull') return `${name} — пусто`
      if (this.newCond.sourceType === 'paramentity') return `${name} ${op} параметр формы`
      if (this.newCond.sourceType === 'scalar') return `${name} ${op} скаляр #${this.newCond.scalarId || '?'}`
      if (this.selectedFieldDatatype === 'bit') return `${name} ${op} ${this.newCond.boolValue}`
      return `${name} ${op} "${this.newCond.value || '...'}"`
    },
    suggestions() {
      const existing = new Set(this.conditions.map(c => c.args && c.args[0] && (typeof c.args[0].value === 'object' ? c.args[0].value?.value : c.args[0].value)))
      const sugg = []
      if (!existing.has(11)) {
        sugg.push({
          label: 'Скрыть удалённые записи', field: 11, oper: 'equal', type: 'value', value: false,
        })
      }
      this.params.forEach(p => {
        if (!existing.has(p.entityId)) {
          sugg.push({
            label: `Фильтровать по ${p.name}`, field: p.entityId, oper: 'equal', type: 'paramentity', value: p.entityId,
          })
        }
      })
      this.parentResults.forEach(r => {
        if (r.destinationEntityId && !existing.has(r.destinationEntityId)) {
          sugg.push({
            label: `Фильтровать по ${this.entName(r.destinationEntityId)} (из группировки)`, field: r.destinationEntityId, oper: 'equal', type: 'paramentity', value: r.destinationEntityId,
          })
        }
      })
      return sugg
    },
  },
  methods: {
    entName(id) {
      if (!id) return '?'
      const info = this.entityMap[id]
      if (info) return info.name
      const pf = this.paletteFields.find(f => f.entityId === id)
      return pf ? pf.name : `#${id}`
    },
    condSentence(cond) {
      const a0 = this.argText(cond, 0)
      const op = this.operText(cond)
      const a1 = this.argText(cond, 1)
      if (!cond.args || cond.args.length < 2) return `${a0} ${op}`
      return `${a0} ${op} ${a1}`
    },
    condDetail(cond) {
      if (!cond.args || !cond.args[1]) return ''
      const t = cond.args[1].type && cond.args[1].type.sid
      if (t === 'paramentity') return 'Берётся из параметра формы автоматически'
      if (t === 'scalar') return 'Вычисляется скаляром на сервере'
      if (t === 'formula') return 'Вычисляется формулой'
      return ''
    },
    argText(cond, idx) {
      if (!cond.args || !cond.args[idx]) return ''
      const arg = cond.args[idx]
      const t = arg.type && arg.type.sid
      if (t === 'entity') {
        const val = typeof arg.value === 'object' ? (arg.value && arg.value.value) : arg.value
        return this.entName(val)
      }
      if (t === 'paramentity') return this.entName(arg.value)
      if (t === 'value') {
        if (arg.value === false) return 'нет (false)'
        if (arg.value === true) return 'да (true)'
        if (arg.value === null || arg.value === undefined) return 'пусто'
        return String(arg.value)
      }
      if (t === 'scalar') return `скаляр #${typeof arg.value === 'object' ? arg.value?.value : arg.value}`
      if (t === 'formula') return `формула #${typeof arg.value === 'object' ? arg.value?.value : arg.value}`
      return String(arg.value || '')
    },
    operText(cond) {
      if (!cond.oper) return '?'
      const m = {
        equal: 'равно', notequal: 'не равно', greater: 'больше', less: 'меньше', like: 'содержит', isnull: 'пустое', isnotnull: 'не пустое', startwith: 'начинается с', inbranch: 'в ветке', currentuser: 'текущий пользователь', exists: 'существует',
      }
      return m[cond.oper.sid] || cond.oper.name || cond.oper.sid
    },
    condTag(cond) {
      if (!cond.args || !cond.args[1]) return ''
      const t = cond.args[1].type && cond.args[1].type.sid
      if (t === 'paramentity') return 'авто'
      if (t === 'scalar') return 'скаляр'
      if (t === 'formula') return 'формула'
      return ''
    },
    toggleCond(ci, active) {
      const conds = [...this.conditions]
      conds[ci] = { ...conds[ci], isactive: active }
      this.emitUpdate(conds)
    },
    removeCond(ci) {
      const conds = [...this.conditions]
      conds.splice(ci, 1)
      this.emitUpdate(conds)
    },
    applySuggestion(s) {
      const cond = this.buildCond(s.field, s.oper, s.type, s.value)
      this.emitUpdate([...this.conditions, cond])
    },
    addCondition() {
      if (!this.canAdd) return
      let val
      if (this.newCond.oper === 'isnull') {
        val = null
      } else if (this.newCond.sourceType === 'paramentity') {
        val = this.newCond.field
      } else if (this.newCond.sourceType === 'scalar') {
        val = this.newCond.scalarId
      } else if (this.selectedFieldDatatype === 'bit') {
        val = this.newCond.boolValue
      } else {
        val = this.newCond.value
        if (!isNaN(val) && val !== '') val = Number(val)
      }
      const sourceType = this.newCond.oper === 'isnull' ? 'value' : this.newCond.sourceType
      this.emitUpdate([...this.conditions, this.buildCond(this.newCond.field, this.newCond.oper, sourceType, val)])
      this.showAddForm = false
      this.newCond = {
        field: null, oper: 'equal', sourceType: 'paramentity', value: '', boolValue: false, scalarId: null,
      }
    },
    buildCond(fieldId, oper, sourceType, value) {
      return { isactive: true, oper: { sid: oper }, args: [{ type: { sid: 'entity' }, value: fieldId }, { type: { sid: sourceType }, value }] }
    },
    emitUpdate(conds) {
      const filter = this.filter ? JSON.parse(JSON.stringify(this.filter)) : {
        isActive: true,
        node: {
          isactive: true, oper: { sid: 'and' }, nodes: [], conds: [],
        },
      }
      filter.node.conds = conds
      this.$emit('update', filter)
    },
  },
}
</script>

<style scoped>
.fdf { font-size: 12px; }
.fdf-list { display: flex; flex-direction: column; gap: 4px; margin-bottom: 10px; }
.fdf-cond { display: flex; align-items: flex-start; gap: 8px; padding: 6px 10px; background: #f8f9fa; border: 1px solid #eee; border-radius: 6px; }
.fdf-cond--off { opacity: 0.5; }
.fdf-cond-check { margin-top: 2px; flex-shrink: 0; }
.fdf-cond-body { flex: 1; }
.fdf-cond-sentence { font-size: 12px; color: #333; }
.fdf-cond-sentence strong:first-child { color: #1565c0; }
.fdf-cond-sentence strong:last-child { color: #2e7d32; }
.fdf-cond-detail { font-size: 10px; color: #999; margin-top: 1px; }
.fdf-cond-tag { font-size: 9px; padding: 2px 7px; background: #e8f5e9; color: #2e7d32; border-radius: 10px; flex-shrink: 0; margin-top: 2px; }
.fdf-cond-del { cursor: pointer; color: #ccc; font-size: 12px; flex-shrink: 0; margin-top: 2px; }
.fdf-cond-del:hover { color: #e74c3c; }
.fdf-empty { color: #bbb; padding: 10px 0; display: flex; align-items: center; }
.fdf-suggest { margin-bottom: 10px; }
.fdf-suggest-title { font-size: 10px; color: #888; text-transform: uppercase; margin-bottom: 4px; letter-spacing: 0.5px; }
.fdf-suggest-item { display: flex; align-items: center; gap: 6px; padding: 5px 10px; color: #555; cursor: pointer; border-radius: 4px; transition: all 0.15s; }
.fdf-suggest-item:hover { background: #e8f5e9; color: #2e7d32; }
.fdf-suggest-icon { color: #66bb6a; }
.fdf-custom { margin-bottom: 8px; }
.fdf-add-btn { display: inline-flex; align-items: center; gap: 5px; padding: 5px 14px; font-size: 12px; color: #666; background: #f0f0f0; border: 1px solid #ddd; border-radius: 4px; cursor: pointer; }
.fdf-add-btn:hover { background: #e0e0e0; }
.fdf-add-form { border: 1px solid #ddd; border-radius: 8px; padding: 12px; background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
.fdf-add-step { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; flex-wrap: wrap; }
.fdf-add-num { width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; background: #7367f0; color: #fff; border-radius: 50%; font-size: 11px; font-weight: 700; flex-shrink: 0; }
.fdf-add-q { font-size: 12px; color: #555; min-width: 160px; }
.fdf-add-select { padding: 5px 8px; font-size: 12px; border: 1px solid #ddd; border-radius: 4px; flex: 1; min-width: 150px; }
.fdf-add-select--source { min-width: 220px; }
.fdf-add-value { margin-top: 4px; margin-left: 28px; }
.fdf-add-input { padding: 5px 8px; font-size: 12px; border: 1px solid #ddd; border-radius: 4px; width: 200px; }
.fdf-add-preview { margin: 8px 0 4px 28px; font-size: 11px; color: #888; padding: 4px 8px; background: #f5f5f5; border-radius: 4px; }
.fdf-add-actions { display: flex; gap: 8px; margin-top: 8px; margin-left: 28px; }
.fdf-btn { padding: 5px 16px; font-size: 12px; border: 1px solid #ddd; border-radius: 4px; cursor: pointer; background: #f5f5f5; color: #666; }
.fdf-btn--primary { background: #7367f0; color: #fff; border-color: #7367f0; }
.fdf-btn--primary:disabled { opacity: 0.5; cursor: default; }
.fdf-footer { display: flex; gap: 16px; padding-top: 8px; border-top: 1px solid #f0f0f0; margin-top: 4px; }
.fdf-footer-link { font-size: 10px; color: #bbb; cursor: pointer; }
.fdf-footer-link:hover { color: #7367f0; }
</style>
